import { useAuthStore } from '@/stores/authStore'
import { useCateringOrders } from '@/hooks/useCatering'
import { useInvoices } from '@/hooks/useBuyers'
import { useInventoryPerishable } from '@/hooks/useInventory'
import { CalendarDays, Users, DollarSign, AlertTriangle, ChevronRight, TrendingUp, Package, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Link } from 'react-router-dom'
import type { CateringStatus, Invoice } from '@/types/app'

const CATERING_STATUS_LABELS: Record<CateringStatus, string> = {
  inquiry:       'Inquiry',
  quoted:        'Quoted',
  deposit_paid:  'Deposit Paid',
  confirmed:     'Confirmed',
  in_production: 'In Production',
  delivered:     'Delivered',
  completed:     'Completed',
  cancelled:     'Cancelled',
}

const CATERING_STATUS_COLORS: Record<CateringStatus, string> = {
  inquiry:       'bg-stone-100 text-stone-600',
  quoted:        'bg-blue-50 text-blue-700',
  deposit_paid:  'bg-amber-50 text-amber-700',
  confirmed:     'bg-brand-50 text-brand-700',
  in_production: 'bg-orange-50 text-orange-700',
  delivered:     'bg-green-50 text-green-700',
  completed:     'bg-green-100 text-green-800',
  cancelled:     'bg-red-50 text-red-600',
}

function greeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

function daysUntil(dateStr: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const event = new Date(dateStr + 'T00:00:00')
  return Math.round((event.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

function daysOverdue(dateStr: string): number {
  return -daysUntil(dateStr)
}

function invoiceTotal(inv: Invoice): number {
  const subtotal = (inv.line_items ?? []).reduce((s, li) => s + li.total, 0)
  const tax = subtotal * (inv.tax_percent ?? 0) / 100
  const discount = inv.discount_amount ?? 0
  return subtotal + tax - discount
}

function fmtMoney(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`
  return `$${n.toFixed(0)}`
}

export function DashboardPage() {
  const { user, restaurant } = useAuthStore()
  const { data: cateringOrders = [] } = useCateringOrders()
  const { data: invoices = [] }       = useInvoices()
  const { data: perishable = [] }     = useInventoryPerishable()

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const in30 = new Date(today)
  in30.setDate(in30.getDate() + 30)

  // Catering metrics
  const activeStatuses: CateringStatus[] = ['inquiry','quoted','deposit_paid','confirmed','in_production']
  const upcomingOrders = cateringOrders
    .filter(o => {
      const d = new Date(o.event_date + 'T00:00:00')
      return d >= today && d <= in30 && activeStatuses.includes(o.status)
    })
    .sort((a, b) => a.event_date.localeCompare(b.event_date))

  const openLeads = cateringOrders.filter(o => o.status === 'inquiry' || o.status === 'quoted').length

  // Invoice metrics
  const openInvoices = invoices.filter(inv => !['paid','cancelled'].includes(inv.status))
  const outstandingTotal = openInvoices.reduce((s, inv) => {
    const total = invoiceTotal(inv)
    return s + (total - (inv.amount_paid ?? 0))
  }, 0)
  const overdueInvoices = invoices.filter(inv => {
    if (['paid','cancelled'].includes(inv.status)) return false
    return inv.due_date && new Date(inv.due_date) < today
  }).sort((a, b) => (a.due_date ?? '').localeCompare(b.due_date ?? ''))

  // Inventory metrics
  const reorderNeeded = perishable.filter(p =>
    p.reorder_point != null && p.quantity <= p.reorder_point
  )

  const stats = [
    {
      label: 'Upcoming Events',
      value: upcomingOrders.length,
      sub: 'next 30 days',
      icon: <CalendarDays size={16} />,
      color: 'text-brand-600 bg-brand-50',
      href: '/catering',
    },
    {
      label: 'Open Leads',
      value: openLeads,
      sub: 'inquiry & quoted',
      icon: <TrendingUp size={16} />,
      color: 'text-blue-600 bg-blue-50',
      href: '/catering',
    },
    {
      label: 'Outstanding',
      value: fmtMoney(outstandingTotal),
      sub: `${openInvoices.length} open invoice${openInvoices.length !== 1 ? 's' : ''}`,
      icon: <DollarSign size={16} />,
      color: 'text-amber-600 bg-amber-50',
      href: '/invoices',
    },
    {
      label: 'Reorder Needed',
      value: reorderNeeded.length,
      sub: 'perishable items low',
      icon: <Package size={16} />,
      color: reorderNeeded.length > 0 ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50',
      href: '/inventory',
    },
  ]

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-stone-900">
          {greeting()}, {user?.full_name.split(' ')[0]}
        </h1>
        <p className="text-sm text-stone-400 mt-0.5">{restaurant?.name}</p>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map(s => (
          <Link
            key={s.label}
            to={s.href}
            className="bg-white rounded-xl border border-stone-200 p-4 flex flex-col gap-2 hover:border-brand-200 hover:shadow-sm transition-all"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-stone-500">{s.label}</p>
              <span className={cn('w-7 h-7 rounded-lg flex items-center justify-center', s.color)}>
                {s.icon}
              </span>
            </div>
            <p className="text-2xl font-bold text-stone-900">{s.value}</p>
            <p className="text-2xs text-stone-400">{s.sub}</p>
          </Link>
        ))}
      </div>

      {/* Main panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Upcoming catering events */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-stone-800">Upcoming Events</h2>
            <Link to="/catering" className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-0.5">
              View all <ChevronRight size={12} />
            </Link>
          </div>

          {upcomingOrders.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <CalendarDays size={28} className="text-stone-300 mx-auto mb-2" />
              <p className="text-sm text-stone-400">No upcoming events in next 30 days</p>
              <Link to="/catering" className="text-xs text-brand-600 hover:underline mt-1 inline-block">
                Add a catering inquiry
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-stone-50">
              {upcomingOrders.slice(0, 6).map(order => {
                const days = daysUntil(order.event_date)
                return (
                  <Link
                    key={order.id}
                    to="/catering"
                    className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors"
                  >
                    {/* Date pill */}
                    <div className={cn(
                      'w-12 text-center rounded-lg py-1.5 shrink-0',
                      days === 0 ? 'bg-red-50' : days <= 3 ? 'bg-orange-50' : 'bg-stone-50'
                    )}>
                      <p className={cn(
                        'text-xs font-bold leading-none',
                        days === 0 ? 'text-red-600' : days <= 3 ? 'text-orange-600' : 'text-stone-700'
                      )}>
                        {new Date(order.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
                      </p>
                      <p className={cn(
                        'text-lg font-bold leading-tight',
                        days === 0 ? 'text-red-700' : days <= 3 ? 'text-orange-700' : 'text-stone-900'
                      )}>
                        {new Date(order.event_date + 'T00:00:00').getDate()}
                      </p>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-stone-900 truncate">{order.client_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {order.guest_count && (
                          <span className="flex items-center gap-1 text-xs text-stone-400">
                            <Users size={10} /> {order.guest_count}
                          </span>
                        )}
                        {order.event_time && (
                          <span className="flex items-center gap-1 text-xs text-stone-400">
                            <Clock size={10} /> {order.event_time}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={cn(
                        'text-2xs font-medium px-2 py-0.5 rounded-full',
                        CATERING_STATUS_COLORS[order.status]
                      )}>
                        {CATERING_STATUS_LABELS[order.status]}
                      </span>
                      <span className={cn(
                        'text-2xs font-medium',
                        days === 0 ? 'text-red-600' : days <= 3 ? 'text-orange-500' : 'text-stone-400'
                      )}>
                        {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days}d away`}
                      </span>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100">
            <h2 className="text-sm font-semibold text-stone-800">Alerts</h2>
          </div>

          {overdueInvoices.length === 0 && reorderNeeded.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-2">
                <span className="text-green-600 text-lg">✓</span>
              </div>
              <p className="text-sm text-stone-400">No alerts. You're all clear.</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-50">

              {/* Overdue invoices */}
              {overdueInvoices.slice(0, 4).map(inv => {
                const total = invoiceTotal(inv)
                const outstanding = total - (inv.amount_paid ?? 0)
                const od = daysOverdue(inv.due_date!)
                return (
                  <Link
                    key={inv.id}
                    to="/invoices"
                    className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors"
                  >
                    <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                      <AlertTriangle size={13} className="text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">
                        {inv.buyer?.name ?? 'Unknown buyer'}
                      </p>
                      <p className="text-xs text-stone-400">{inv.invoice_number} · {od}d overdue</p>
                    </div>
                    <p className="text-sm font-bold text-red-600 shrink-0">${outstanding.toFixed(0)}</p>
                  </Link>
                )
              })}

              {/* Low stock / reorder items */}
              {reorderNeeded.slice(0, 4).map(p => (
                <Link
                  key={p.id}
                  to="/inventory"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors"
                >
                  <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                    <Package size={13} className="text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">
                      {(p.ingredient as { name?: string } | undefined)?.name ?? 'Unknown item'}
                    </p>
                    <p className="text-xs text-stone-400">
                      {p.quantity} {p.unit} on hand · reorder at {p.reorder_point}
                    </p>
                  </div>
                  <span className="text-2xs font-medium bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full shrink-0">
                    Reorder
                  </span>
                </Link>
              ))}

              {(overdueInvoices.length > 4 || reorderNeeded.length > 4) && (
                <div className="px-4 py-2 text-center">
                  <p className="text-xs text-stone-400">
                    +{Math.max(0, overdueInvoices.length - 4) + Math.max(0, reorderNeeded.length - 4)} more alerts
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Catering pipeline summary */}
      {cateringOrders.some(o => activeStatuses.includes(o.status)) && (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-stone-800">Catering Pipeline</h2>
            <Link to="/catering" className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-0.5">
              Manage <ChevronRight size={12} />
            </Link>
          </div>
          <div className="px-4 py-3 flex flex-wrap gap-2">
            {(['inquiry','quoted','confirmed','in_production'] as CateringStatus[]).map(status => {
              const count = cateringOrders.filter(o => o.status === status).length
              if (count === 0) return null
              return (
                <div key={status} className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium',
                  CATERING_STATUS_COLORS[status]
                )}>
                  <span>{CATERING_STATUS_LABELS[status]}</span>
                  <span className="font-bold">{count}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
